import React, { useEffect, useState } from 'react'

const initialForm = {
  name: '',
  email: '',
  company: '',
  phone: ''
}

export default function Home () {
  const [form, setForm] = useState(initialForm)
  const [status, setStatus] = useState({ limit: 2, claimed: 0, remaining: 2 })
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState(null)

  useEffect(() => {
    fetch('/api/beta-signup/status')
      .then(res => res.json())
      .then(setStatus)
      .catch(() => {})
  }, [])

  function updateField (event) {
    setForm({
      ...form,
      [event.target.name]: event.target.value
    })
  }

  async function submitSignup (event) {
    event.preventDefault()
    setSubmitting(true)
    setNotice(null)

    try {
      const response = await fetch('/api/beta-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Signup failed.')

      setNotice({
        type: data.status,
        message: data.message
      })
      setStatus(prev => ({
        ...prev,
        remaining: data.remaining,
        claimed: data.status === 'free' && data.message.includes('claimed')
          ? Math.min(prev.limit, prev.claimed + 1)
          : prev.claimed
      }))
      setForm(initialForm)
    } catch (error) {
      setNotice({
        type: 'error',
        message: error.message
      })
    } finally {
      setSubmitting(false)
    }
  }

  const spotsLabel = status.remaining > 0
    ? `${status.remaining} free ${status.remaining === 1 ? 'spot' : 'spots'} left`
    : 'Free spots full'

  return (
    <main className='min-h-screen bg-zinc-950 text-white'>
      {/* Hero + Signup */}
      <section className='mx-auto grid min-h-screen w-full max-w-6xl items-center gap-10 px-6 py-10 lg:grid-cols-[1fr_440px]'>
        <div className='max-w-2xl'>
          <div className='mb-6 inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 text-sm font-medium text-emerald-200'>
            <span className='h-2 w-2 rounded-full bg-emerald-300' />
            {spotsLabel}
          </div>

          <h1 className='text-4xl font-semibold leading-tight md:text-6xl'>
            Bring your own iMessage number into HighLevel.
          </h1>

          <p className='mt-6 max-w-xl text-lg leading-8 text-zinc-300'>
            Use your existing iMessage number and your own Mac to send and receive
            messages inside GHL. No new sender number required. No A2P registration
            needed for iMessage.
          </p>

          <div className='mt-8 grid gap-4 text-sm text-zinc-300 sm:grid-cols-3'>
            <div className='border border-zinc-800 bg-zinc-900/70 p-4'>
              <strong className='block text-white'>Your number</strong>
              Keep the iMessage identity customers already know.
            </div>
            <div className='border border-zinc-800 bg-zinc-900/70 p-4'>
              <strong className='block text-white'>Your Mac</strong>
              Use your own BlueBubbles server as the sender.
            </div>
            <div className='border border-zinc-800 bg-zinc-900/70 p-4'>
              <strong className='block text-white'>Inside GHL</strong>
              Send, receive, and track from the CRM workflow.
            </div>
          </div>
        </div>

        <form onSubmit={submitSignup} className='border border-zinc-800 bg-white p-6 text-zinc-950 shadow-2xl'>
          <div className='mb-6'>
            <p className='text-sm font-semibold uppercase text-emerald-700'>Private beta</p>
            <h2 className='mt-2 text-2xl font-semibold'>Claim a free setup spot</h2>
            <p className='mt-2 text-sm leading-6 text-zinc-600'>
              The first two signups get free access while the product is being shaped.
            </p>
          </div>

          <label className='mb-4 block text-sm font-medium'>
            Name
            <input
              className='mt-2 h-11 w-full border border-zinc-300 px-3 outline-none focus:border-emerald-600'
              name='name'
              value={form.name}
              onChange={updateField}
              autoComplete='name'
              required
            />
          </label>

          <label className='mb-4 block text-sm font-medium'>
            Email
            <input
              className='mt-2 h-11 w-full border border-zinc-300 px-3 outline-none focus:border-emerald-600'
              name='email'
              type='email'
              value={form.email}
              onChange={updateField}
              autoComplete='email'
              required
            />
          </label>

          <label className='mb-4 block text-sm font-medium'>
            Agency or business
            <input
              className='mt-2 h-11 w-full border border-zinc-300 px-3 outline-none focus:border-emerald-600'
              name='company'
              value={form.company}
              onChange={updateField}
              autoComplete='organization'
            />
          </label>

          <label className='mb-5 block text-sm font-medium'>
            Phone
            <input
              className='mt-2 h-11 w-full border border-zinc-300 px-3 outline-none focus:border-emerald-600'
              name='phone'
              value={form.phone}
              onChange={updateField}
              autoComplete='tel'
            />
          </label>

          <button
            className='h-12 w-full bg-zinc-950 px-4 font-semibold text-white disabled:cursor-not-allowed disabled:bg-zinc-500'
            disabled={submitting}
            type='submit'
          >
            {submitting ? 'Saving...' : status.remaining > 0 ? 'Claim free spot' : 'Join waitlist'}
          </button>

          {notice && (
            <div className={`mt-4 border p-3 text-sm ${
              notice.type === 'error'
                ? 'border-red-200 bg-red-50 text-red-700'
                : notice.type === 'free'
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
            >
              {notice.message}
            </div>
          )}
        </form>
      </section>

      {/* How it works */}
      <section className='border-t border-zinc-800 bg-zinc-950 px-6 py-20'>
        <div className='mx-auto max-w-4xl'>
          <h2 className='text-center text-2xl font-semibold md:text-3xl'>How it works</h2>
          <p className='mx-auto mt-4 max-w-xl text-center text-zinc-400'>
            Everything runs through your own Mac and your existing iMessage number.
          </p>

          <div className='mt-12 grid gap-8 md:grid-cols-2'>
            <div className='flex gap-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 font-semibold'>1</div>
              <div>
                <h3 className='font-semibold text-white'>Install in HighLevel</h3>
                <p className='mt-1 text-sm leading-6 text-zinc-400'>
                  Add HostediMessage to your GHL location from the Marketplace.
                </p>
              </div>
            </div>

            <div className='flex gap-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 font-semibold'>2</div>
              <div>
                <h3 className='font-semibold text-white'>Connect GHL</h3>
                <p className='mt-1 text-sm leading-6 text-zinc-400'>
                  Authorize your location so messages can sync into conversations.
                </p>
              </div>
            </div>

            <div className='flex gap-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 font-semibold'>3</div>
              <div>
                <h3 className='font-semibold text-white'>Connect your Mac</h3>
                <p className='mt-1 text-sm leading-6 text-zinc-400'>
                  Link your BlueBubbles server on your Mac. You keep full control of the device.
                </p>
              </div>
            </div>

            <div className='flex gap-4'>
              <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 font-semibold'>4</div>
              <div>
                <h3 className='font-semibold text-white'>Send and receive</h3>
                <p className='mt-1 text-sm leading-6 text-zinc-400'>
                  iMessages send from your number and replies appear back in GHL threads.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer note */}
      <footer className='border-t border-zinc-800 px-6 py-10 text-center text-xs text-zinc-500'>
        <p>
          HostediMessage is not affiliated with Apple. iMessage is a service mark of Apple Inc.
        </p>
        <p className='mt-2'>
          You are responsible for your own Mac, BlueBubbles server, and compliance with applicable messaging laws.
        </p>
      </footer>
    </main>
  )
}
